// src/pages/Devices.js
import React, { useEffect, useState, useCallback } from "react";
import { Dialog } from "@headlessui/react";
import {
  Router,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XIcon,
  SearchIcon,
} from "lucide-react";
import api from "../services/api";

const Devices = () => {
  const [devices, setDevices] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [formData, setFormData] = useState({
    nama: "",
    ip_address: "",
    port: "",
    bts_info: "",
    additional_info: "",
    lokasi: "",
  });
  const [searchKey, setSearchKey] = useState("nama");
  const [searchValue, setSearchValue] = useState("");
  const token = localStorage.getItem("token");
  const [loading, setLoading] = useState(true);

  const fetchDevices = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/devices`, {
        headers: { authorization: token },
      });
      if (response.data.status === 0) {
        setDevices(response.data.data);
      } else {
        console.error("Gagal mendapatkan data devices:", response.data.message);
      }
    } catch (error) {
      console.error("Failed to fetch devices:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const openAddModal = () => {
    setFormData({
      nama: "",
      ip_address: "",
      port: "",
      bts_info: "",
      additional_info: "",
      lokasi: "",
    });
    setIsEditMode(false);
    setIsModalOpen(true);
  };

  const openEditModal = (device) => {
    setFormData({
      nama: device.nama,
      ip_address: device.ip_address,
      port: device.port,
      bts_info: device.bts_info,
      additional_info: device.additional_info,
      lokasi: device.lokasi,
    });
    setSelectedDevice(device);
    setIsEditMode(true);
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    setLoading(true);
    e.preventDefault();
    try {
      if (isEditMode && selectedDevice) {
        // Update
        const response = await api.put(
          `/devices/${selectedDevice.id}`,
          formData,
          {
            headers: { authorization: token },
          }
        );
        if (response.data.status === 0) {
          alert("Device berhasil diperbarui!");
          fetchDevices();
          setIsModalOpen(false);
        }
      } else {
        // Add new
        const response = await api.post(`/devices`, formData, {
          headers: { authorization: token },
        });
        if (response.data.status === 0) {
          alert("Device berhasil ditambahkan!");
          fetchDevices();
          setIsModalOpen(false);
        }
      }
    } catch (error) {
      console.error("Failed to save device:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    setLoading(true);
    if (!window.confirm("Yakin ingin menghapus device ini?")) return;
    try {
      const response = await api.delete(`/devices/${id}`, {
        headers: { authorization: token },
      });
      if (response.data.status === 0) {
        alert("Device berhasil dihapus!");
        fetchDevices();
      }
    } catch (error) {
      console.error("Failed to delete device:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    setLoading(true);
    try {
      const response = await api.get(
        `/devices/search-like?key=${searchKey}&value=${searchValue}`,
        { headers: { authorization: token } }
      );
      if (response.data.status === 0) {
        setDevices(response.data.data);
      } else {
        alert("Device tidak ditemukan");
      }
    } catch (error) {
      console.error("Failed to search devices:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="w-16 h-16 border-4 border-blue-500 border-dashed rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Router /> Devices
        </h1>
        <button
          onClick={openAddModal}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          <PlusIcon size={16} /> Add Device
        </button>
      </div>

      {/* Search */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSearch();
        }}
        className="flex gap-2 mb-6"
      >
        <select
          value={searchKey}
          onChange={(e) => setSearchKey(e.target.value)}
          className="border text-black dark:text-white border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400"
        >
          {[
            "id",
            "nama",
            "ip_address",
            "port",
            "bts_info",
            "additional_info",
            "lokasi",
            "created_at",
            "updated_at",
            "status",
          ].map((key) => (
            <option key={key} value={key}>
              {key}
            </option>
          ))}
        </select>
        <input
          type="text"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          placeholder="Cari..."
          className="w-full border text-black dark:text-white border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400 flex-1"
        />
        <button
          type="submit"
          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded flex items-center gap-2"
        >
          <SearchIcon size={16} /> Search
        </button>
      </form>

      {/* Device list */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {devices.map((device) => (
          <div
            key={device.id}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-md p-6 flex flex-col justify-between hover:shadow-lg transition-shadow duration-300 relative"
          >
            <div>
              <h2 className="text-xl font-bold mb-2 text-gray-800 dark:text-white">
                {device.nama}
              </h2>
              <div className="space-y-1 text-gray-600 dark:text-gray-300 text-sm">
                <p>
                  <span className="font-medium">IP:</span> {device.ip_address}
                </p>
                <p>
                  <span className="font-medium">Lokasi:</span> {device.lokasi}
                </p>
                <div className="flex items-center gap-2 mt-2">
                  <span className="font-medium">Status:</span>
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                      device.status === "online"
                        ? "bg-green-100 text-green-600"
                        : "bg-red-100 text-red-600"
                    }`}
                  >
                    {device.status}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between mt-5">
              <div className="flex gap-2">
                <button
                  onClick={() => openEditModal(device)}
                  className="flex items-center gap-1 px-3 py-2 text-blue-600 hover:text-blue-800 rounded-md border border-blue-600 hover:border-blue-800 text-sm transition"
                >
                  <PencilIcon size={16} /> Edit
                </button>
                <button
                  onClick={() => handleDelete(device.id)}
                  className="flex items-center gap-1 px-3 py-2 text-red-600 hover:text-red-800 rounded-md border border-red-600 hover:border-red-800 text-sm transition"
                >
                  <TrashIcon size={16} /> Delete
                </button>
              </div>

              <p className="text-[10px] text-gray-400 dark:text-gray-500 text-right">
                Updated: {new Date(device.updated_at).toLocaleDateString()}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      <Dialog
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        className="fixed z-50 inset-0 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Panel className="bg-white dark:bg-gray-600 p-6 rounded-lg w-full max-w-md relative">
            <button
              onClick={() => setIsModalOpen(false)}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
            >
              <XIcon size={20} />
            </button>
            <Dialog.Title className="text-lg font-bold text-black dark:text-white mb-4">
              {isEditMode ? "Edit Device" : "Add Device"}
            </Dialog.Title>
            <form onSubmit={handleSubmit} className="space-y-3">
              {[
                "nama",
                "ip_address",
                "port",
                "bts_info",
                "additional_info",
                "lokasi",
              ].map((key) => (
                <input
                  key={key}
                  name={key}
                  placeholder={key.replace("_", " ")}
                  value={formData[key]}
                  onChange={handleChange}
                  className="w-full border text-black dark:text-white border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400"
                  required
                />
              ))}
              <button
                type="submit"
                className="w-full bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                {isEditMode ? "Update Device" : "Add Device"}
              </button>
            </form>
          </Dialog.Panel>
        </div>
      </Dialog>
    </div>
  );
};

export default Devices;
